import { SelectChip, Typography, type SxProps, type Theme } from "~/common/mui";
import { ControlField } from "./ControlField";
import { ControlInfo } from "./ControlInfo";
import { ControlOptionLabel } from "./ControlOptionLabel";
import type { SelectChipControlViewModel } from "./controlViewModel";

export interface SelectChipControlProps extends Omit<SelectChipControlViewModel, "type" | "controlIndex"> {
  onChange: (value: string | null) => void;
  className?: string;
  sx?: SxProps<Theme>;
}

export const SelectChipControl = ({
  label,
  hideLabel,
  info,
  helperText,
  error,
  errorText,
  disabled = false,
  value,
  options,
  standardLabel,
  onChange,
  className,
  sx,
}: SelectChipControlProps) => {
  if (options.filter((option) => !option.disabled).length === 1) {
    return null;
  }

  const hasDefaultValue = options.some((option) => option.standard);

  return (
    <ControlField
      label={label}
      helperText={helperText}
      error={Boolean(error)}
      errorText={errorText}
      hideLabel={hideLabel ?? true}
      className={className}
      sx={sx}
    >
      <SelectChip
        value={value ?? ""}
        disabled={disabled}
        values={options.map((option) => ({
          value: option.value,
          label: <ControlOptionLabel option={option} standardLabel={standardLabel} />,
          selectedLabel: (
            <Typography component="strong" variant="bodyMedium" sx={{ fontWeight: 700 }}>
              {option.label}
            </Typography>
          ),
          disabled: option.disabled,
        }))}
        showSelectedIcon
        filterLabel={label}
        clearLabel={`Clear ${label}`}
        onClear={hasDefaultValue ? undefined : () => onChange(null)}
        onChange={(_, selectedValue) => onChange(selectedValue)}
        endAdornment={info ? <ControlInfo {...info} /> : undefined}
        sx={(theme) => ({
          borderColor: error ? theme.palette.error.main : undefined,
        })}
      />
    </ControlField>
  );
};
